/**
 * nativeInit — runs once after sign-in on iOS to:
 *   1. Register for push notifications and persist the device token
 *      (locally + Supabase upsert into user_devices).
 *   2. Subscribe to push taps and route the user to the deep-link in
 *      the notification payload (`data.route` → window.location.hash).
 *   3. Subscribe to app-resume events and (if Face ID is enabled in
 *      Settings) prompt before revealing the dashboard.
 *
 * No-op on web. Safe to call multiple times — internal flags prevent
 * double-registration.
 */
import { isNative } from "./platform";
import {
  registerForPush,
  onPushTap,
  biometricAvailable,
  biometricVerify,
  onAppResume,
} from "./nativeBridge";
import { supabase } from "../supabaseClient";

const TOKEN_KEY = "bam_push_token";

async function describeVersion() {
  const parts = [];
  try {
    const { App } = await import("@capacitor/app");
    const info = await App.getInfo();
    if (info?.version) parts.push(`${info.version}${info.build ? ` (${info.build})` : ""}`);
  } catch {}
  try {
    const { CapacitorUpdater } = await import("@capgo/capacitor-updater");
    const cur = await CapacitorUpdater.current();
    const v = cur?.bundle?.version;
    if (v) parts.push(v === "builtin" ? "bundled" : v);
  } catch {}
  return parts.length ? parts.join(" · ") : null;
}
const FACE_ID_PREF = "bam_face_id_enabled";

let _registered = false;
let _tapListener = null;
let _resumeListener = null;
let _resumeLockUntil = 0;

async function persistToken(userId, token) {
  if (!token) return;
  try { localStorage.setItem(TOKEN_KEY, token); } catch {}
  if (!userId) return;
  // Best-effort upsert. If the user_devices table doesn't exist yet
  // (migration not applied), the error is swallowed silently — we just
  // keep the token in localStorage so the next call can retry.
  try {
    const platform = "ios"; // we only register on native; getPlatform() also fine
    // "1.0 (12) · 2.7.311": the store build and the OTA bundle it's running.
    // window.Capacitor.appVersion was never a thing, so this column sat
    // null and told us nothing when a phone looked stuck on an old build.
    const appVersion = await describeVersion();
    await supabase
      .from("user_devices")
      .upsert(
        { user_id: userId, platform, token, app_version: appVersion, last_seen_at: new Date().toISOString() },
        { onConflict: "user_id,token" }
      );
  } catch { /* table may not exist in this env yet */ }
}

export async function initNativeOnSignIn(userId) {
  if (!isNative()) return;
  if (_registered) return;
  _registered = true;

  // ── RE-ENABLED for v1 (2026-06) ──
  // The boot WebContent crash root cause was found + fixed (unvirtualized
  // 632-item lists, now windowed). Native init (push registration + listeners)
  // is back ON. Two safety measures kept:
  //   1. A no-rebuild KILL SWITCH: set localStorage["bam_disable_native_init"]="1"
  //      (e.g. from the Xcode JS console) to turn this off instantly if
  //      anything regresses on device.
  //   2. A short DEFER so the native plugin calls don't fire inside the first
  //      frames (the iOS watchdog window) — belt-and-suspenders.
  try {
    if (typeof window === "undefined" || localStorage.getItem("bam_disable_native_init") === "1") return;
  } catch { return; }
  await new Promise(r => setTimeout(r, 1500));

  // 1. Push registration
  try {
    const token = await registerForPush();
    if (token) await persistToken(userId, token);
  } catch { /* swallow */ }

  // 2. Tap handler — read `data.route` (+ optional `data.postId`) from the
  //    push payload. Two delivery paths, because setting the hash alone is
  //    lossy on cold starts (boot re-parses the hash and can land on
  //    dashboard before the app is ready to navigate):
  //      a) stash in localStorage — BAMFull consumes it once boot settles
  //      b) window event — BAMFull applies it immediately when already open
  try {
    _tapListener?.remove?.();
    _tapListener = await onPushTap((data) => {
      const target = data?.route || data?.url || data?.hash;
      if (typeof target === "string" && target.startsWith("#/")) {
        // drillId/sectionId (optional) open one specific item inside the
        // section the route points at. A payload without them behaves
        // exactly as before — the route alone still lands on the section.
        const detail = {
          route: target,
          postId: data?.postId || null,
          drillId: data?.drillId || null,
          sectionId: data?.sectionId || null,
          qaId: data?.qaId || null,
        };
        try { localStorage.setItem("bam_push_route", JSON.stringify({ ...detail, at: Date.now() })); } catch {}
        try { window.location.hash = target; } catch {}
        try { window.dispatchEvent(new CustomEvent("bam-push-route", { detail })); } catch {}
      }
    });
  } catch { /* swallow */ }

  // 3. App-resume Face ID gate — only re-prompts if the user has
  //    explicitly opted in (Settings → Face ID toggle). 60-second grace
  //    window so quick app switches don't spam the prompt.
  //
  // IMPORTANT: Face ID failure (user cancel, biometric not available
  // at runtime, OS-level prompt error) MUST NOT auto-signOut. Previous
  // version did that — and on dev/TestFlight builds the prompt fails
  // silently around system overlays (share sheet, push permission,
  // anything that briefly backgrounds the app), causing a sign-out
  // cascade: SIGNED_OUT → redirect to #/signin → supabase auto-restore
  // from localStorage → SIGNED_IN → initNativeOnSignIn re-runs →
  // onAppResume re-fires → loop. The "Verifying membership" BootLoader
  // flashes on each cycle.
  //
  // New behavior: on failure, set a 30s lockout to suppress repeat
  // prompts but DON'T touch the session. The user stays on whatever
  // screen they were on; their session is unaffected.
  try {
    _resumeListener?.remove?.();
    _resumeListener = await onAppResume(async () => {
      if (Date.now() < _resumeLockUntil) return;
      let enabled = false;
      try { enabled = localStorage.getItem(FACE_ID_PREF) === "1"; } catch {}
      if (!enabled) return;
      const { available } = await biometricAvailable();
      if (!available) return;
      const ok = await biometricVerify({ reason: "Unlock BAM Coaches" });
      if (!ok) {
        // Soft lockout — no sign-out, no redirect. Give the user 30s
        // before we prompt again so they can interact normally.
        _resumeLockUntil = Date.now() + 30_000;
      } else {
        _resumeLockUntil = Date.now() + 60_000;
      }
    });
  } catch { /* swallow */ }
}

// Emergency escape hatch — call from the browser/Xcode JS console if a
// coach is locked out of the app by a misbehaving Face ID prompt:
//   window.__bamDisableFaceId()
// Clears the preference and removes the resume listener so the next
// app open won't prompt. Settings → Face ID toggle will re-enable it.
if (typeof window !== "undefined") {
  window.__bamDisableFaceId = () => {
    try { localStorage.removeItem(FACE_ID_PREF); } catch {}
    try { _resumeListener?.remove?.(); } catch {}
    _resumeListener = null;
    return "Face ID disabled. Restart the app.";
  };
}

export function teardownNative() {
  try { _tapListener?.remove?.(); } catch {}
  try { _resumeListener?.remove?.(); } catch {}
  _tapListener = null;
  _resumeListener = null;
  _registered = false;
}
